import { POSE_CONNECTIONS } from '@mediapipe/pose'


/**
 * Draws exercise poses to canvas independently of camera capture
 * @param canvas - Canvas element
 * @param exerciseData - Exercise data containing keyposes
 * @param currentKeyposeIndex - Index of current keypose to highlight
 */
export function drawExerciseToCanvas (
  canvas: HTMLCanvasElement,
  exerciseData: any,
  currentKeyposeIndex = 0,
): void {
  if (!canvas || !exerciseData || !exerciseData.keyposes || exerciseData.keyposes.length === 0) {
    return
  }

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return
  }

  // Save current canvas state
  ctx.save()

  // Clear the canvas
  ctx.clearRect(0, 0, canvas.width, canvas.height)

  // Canvas dimensions should already be set by the WebcamDisplay component
  // Only set default dimensions if canvas is somehow not initialized
  if (canvas.width === 0 || canvas.height === 0) {
    canvas.width = 1280
    canvas.height = 720
  }

  // Draw exercise keyposes with manual coordinate conversion
  drawExerciseKeyposesManual(ctx, exerciseData.keyposes, currentKeyposeIndex, canvas.width, canvas.height)

  // Restore canvas state
  ctx.restore()
}

/**
 * Manually draws exercise keyposes with proper coordinate conversion
 * @param ctx - Canvas 2D context
 * @param keyposes - Array of exercise keypose data
 * @param currentIndex - Index of the current keypose to highlight
 * @param canvasWidth - Canvas width
 * @param canvasHeight - Canvas height
 */
export function drawExerciseKeyposesManual (
  ctx: CanvasRenderingContext2D,
  keyposes: any[],
  currentIndex = 0,
  canvasWidth: number,
  canvasHeight: number,
): void {
  if (!keyposes || keyposes.length === 0) return

  keyposes.forEach((keypose, index) => {
    const isCurrent = index === currentIndex
    const color = isCurrent ? '#0030FF' : '#00FFFF60' // Magenta for current, cyan for others (more visible)
    const lineWidth = isCurrent ? 5 : 3 // Thicker lines for better visibility
    if (isCurrent) {
      ctx.globalCompositeOperation = 'source-over'
    }
    drawExercisePoseManual(ctx, keypose, color, lineWidth, canvasWidth, canvasHeight)
    if (isCurrent) {
      ctx.globalCompositeOperation = 'destination-over'
    }
  })
}

/**
 * Manually draws a single exercise pose with coordinate conversion
 * @param ctx - Canvas 2D context
 * @param keypose - Exercise keypose data
 * @param color - Color for drawing the pose
 * @param lineWidth - Line width for connections
 * @param canvasWidth - Canvas width
 * @param canvasHeight - Canvas height
 */
function drawExercisePoseManual (
  ctx: CanvasRenderingContext2D,
  keypose: any,
  color: string,
  lineWidth: number,
  canvasWidth: number,
  canvasHeight: number,
): void {
  if (!keypose) return

  // Convert keypose data to canvas coordinates
  const landmarks = convertKeyposeToCanvasCoordinates(keypose, canvasWidth, canvasHeight)

  if (landmarks.length === 0) return

  // Set drawing properties
  ctx.strokeStyle = color
  ctx.fillStyle = color
  ctx.lineWidth = lineWidth
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  // Draw connections (skeleton)
  for (const [startIdx, endIdx] of POSE_CONNECTIONS) {
    if (startIdx < landmarks.length && endIdx < landmarks.length) {
      const start = landmarks[startIdx]
      const end = landmarks[endIdx]

      if (start && end && start.visibility > 0.5 && end.visibility > 0.5) {
        ctx.beginPath()
        ctx.moveTo(start.x, start.y)
        ctx.lineTo(end.x, end.y)
        ctx.stroke()
      }
    }
  }

  // Draw landmarks (joints)
  landmarks.forEach((landmark) => {
    if (landmark && landmark.visibility > 0.5) {
      ctx.beginPath()
      ctx.arc(landmark.x, landmark.y, 3, 0, 2 * Math.PI)
      ctx.fill()
    }
  })
}

/**
 * Converts keypose data to canvas coordinates
 * @param keypose - Exercise keypose data
 * @param canvasWidth - Canvas width
 * @param canvasHeight - Canvas height
 * @returns Array of landmarks with canvas coordinates
 */
function convertKeyposeToCanvasCoordinates (
  keypose: any,
  canvasWidth: number,
  canvasHeight: number,
): any[] {
  if (!keypose) return []

  // Handle different keypose data formats
  let keypointData: any[] = []

  if (Array.isArray(keypose)) {
    keypointData = keypose
  } else if (keypose.data && Array.isArray(keypose.data)) {
    keypointData = keypose.data
  } else if (keypose.keypoints && Array.isArray(keypose.keypoints)) {
    keypointData = keypose.keypoints
  } else {
    return []
  }

  const landmarks: any[] = []

  for (let i = 0; i < 33; i++) { // MediaPipe has 33 landmarks
    if (i < keypointData.length && Array.isArray(keypointData[i])) {
      const keypoint = keypointData[i]
      // PoseData format: [x, y, z, confidence, x2d, y2d, z2d]
      // Use 2D coordinates (indices 4, 5, 6) and convert to canvas coordinates
      const x2d = keypoint[4] || 0 // Normalized x (0-1)
      const y2d = keypoint[5] || 0 // Normalized y (0-1)
      const z2d = keypoint[6] || 0 // Normalized z (0-1)
      const visibility = keypoint[3] || 1.0

      landmarks.push({
        x: x2d * canvasWidth,      // Convert to canvas x coordinate
        y: y2d * canvasHeight,     // Convert to canvas y coordinate
        z: z2d,
        visibility: visibility,
      })
    } else {
      // Fill missing landmarks with default values
      landmarks.push({
        x: 0,
        y: 0,
        z: 0,
        visibility: 0,
      })
    }
  }

  return landmarks
}
